import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MainContainer from "../../components/MainContainer";
import AppPageTitle from "../../components/AppPageTitle";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { exportPdfReport } from "./PdfPrint";
import { ExcelPrint } from "./ExcelPrint";

const DEFAULT_FILTERS = {
	searchParam: "",
	period: "3",
	status: "all",
	userIds: [],
	queueIds: [],
	connectionIds: [],
	tagIds: [],
	groupBy: "userId",
};

const PERIOD_OPTIONS = [
	{ value: "0", label: "Todo o período" },
	{ value: "1", label: "Hoje" },
	{ value: "2", label: "Ontem" },
	{ value: "3", label: "Últimos 7 dias" },
	{ value: "4", label: "Últimos 30 dias" },
	{ value: "5", label: "Este mês" },
	{ value: "6", label: "Últimos 6 meses" },
	{ value: "7", label: "Este ano" },
];

const STATUS_OPTIONS = [
	{ value: "all", label: "Todos os status" },
	{ value: "open", label: "Abertos" },
	{ value: "pending", label: "Pendentes" },
	{ value: "closed", label: "Fechados" },
];

const GROUP_BY_OPTIONS = [
	{ value: "userId", label: "Atendente" },
	{ value: "queueId", label: "Setor" },
	{ value: "whatsappId", label: "Conexão" },
	{ value: "status", label: "Status" },
];

const STATUS_META = {
	open: {
		label: "Aberto",
		className: "bg-primary/10 text-primary",
	},
	pending: {
		label: "Pendente",
		className: "bg-warning/10 text-warning",
	},
	closed: {
		label: "Fechado",
		className: "bg-success/10 text-success",
	},
};

function IconSearch() {
	return (
		<svg className="size-4 text-default-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="11" cy="11" r="8" />
			<path d="m21 21-4.35-4.35" />
		</svg>
	);
}

function IconChevronDown() {
	return (
		<svg className="size-4 shrink-0 text-default-500 dark:text-default-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="m6 9 6 6 6-6" />
		</svg>
	);
}

function IconRefresh() {
	return (
		<svg className="size-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M21 2v6h-6" />
			<path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
			<path d="M3 22v-6h6" />
			<path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
		</svg>
	);
}

function IconDownload() {
	return (
		<svg className="size-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M12 3v12" />
			<path d="m7 10 5 5 5-5" />
			<path d="M5 21h14" />
		</svg>
	);
}

function IconSheet() {
	return (
		<svg className="size-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
			<path d="M14 2v6h6" />
			<path d="M8 13h8" />
			<path d="M8 17h8" />
			<path d="M8 9h2" />
		</svg>
	);
}

function IconEraser() {
	return (
		<svg className="size-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="m7 21 10-10" />
			<path d="m5 19 2 2" />
			<path d="m14 4 6 6" />
			<path d="M9 3 3 9a2.83 2.83 0 0 0 0 4l8 8h7" />
		</svg>
	);
}

function IconTicket() {
	return (
		<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M3 10V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4" />
			<path d="M3 14v4a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-4" />
			<path d="M14 4v16" />
			<path d="M10 9h.01" />
			<path d="M10 15h.01" />
		</svg>
	);
}

function IconLayers() {
	return (
		<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="m12 3-9 4.5 9 4.5 9-4.5L12 3Z" />
			<path d="m3 12 9 4.5 9-4.5" />
			<path d="m3 16.5 9 4.5 9-4.5" />
		</svg>
	);
}

function IconTag() {
	return (
		<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M20.59 13.41 11 3H4v7l9.59 9.59a2 2 0 0 0 2.82 0l4.18-4.18a2 2 0 0 0 0-2.82Z" />
			<path d="M7 7h.01" />
		</svg>
	);
}

function IconNote() {
	return (
		<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
			<path d="M8 9h8" />
			<path d="M8 13h5" />
		</svg>
	);
}

function IconUserX() {
	return (
		<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
			<circle cx="9" cy="7" r="4" />
			<path d="m17 8 5 5" />
			<path d="m22 8-5 5" />
		</svg>
	);
}

function formatDateTime(value) {
	if (!value) return "-";
	return new Date(value).toLocaleString("pt-BR");
}

function normalizeText(value) {
	return (value || "").replace(/\s+/g, " ").trim();
}

function getStatusMeta(status) {
	return (
		STATUS_META[status] || {
			label: status || "Sem status",
			className:
				"bg-default-200 text-default-600 dark:bg-default-700 dark:text-default-300",
		}
	);
}

function getGroupLabel(groupBy, key) {
	if (key === "unassigned") return "Não atribuído";
	if (groupBy === "status") {
		return getStatusMeta(key).label;
	}
	return key;
}

function buildFileName(extension) {
	const stamp = new Date().toLocaleDateString("pt-BR").replace(/\//g, "-");
	return `relatorio-tickets-${stamp}.${extension}`;
}

function FilterField({ label, hint, children }) {
	return (
		<div className="space-y-2">
			<label className="block text-sm font-medium text-default-700 dark:text-default-200">
				{label}
			</label>
			{children}
			{hint ? (
				<p className="m-0 text-xs text-default-500 dark:text-default-400">{hint}</p>
			) : null}
		</div>
	);
}

function MultiFilterSelect({
	label,
	options,
	selectedIds,
	onChange,
	placeholder = "Todos",
	emptyText = "Nenhuma opção disponível",
}) {
	const [open, setOpen] = useState(false);
	const containerRef = useRef(null);

	useEffect(() => {
		if (!open) return undefined;

		const handleOutsideClick = event => {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setOpen(false);
			}
		};

		document.addEventListener("mousedown", handleOutsideClick);
		return () => document.removeEventListener("mousedown", handleOutsideClick);
	}, [open]);

	const selectedOptions = options.filter(option =>
		selectedIds.some(id => String(id) === String(option.id))
	);

	const summary =
		selectedOptions.length === 0
			? placeholder
			: selectedOptions.length <= 2
				? selectedOptions.map(option => option.name).join(", ")
				: `${selectedOptions.length} selecionados`;

	const handleToggle = optionId => {
		const exists = selectedIds.some(id => String(id) === String(optionId));
		onChange(
			exists
				? selectedIds.filter(id => String(id) !== String(optionId))
				: [...selectedIds, optionId]
		);
	};

	return (
		<div ref={containerRef} className="relative">
			<FilterField label={label}>
				<button
					type="button"
					className="form-input flex w-full items-center justify-between gap-3 text-left"
					onClick={() => setOpen(current => !current)}
				>
					<span
						className={
							selectedOptions.length > 0
								? "text-default-900 dark:text-default-100"
								: "text-default-500 dark:text-default-400"
						}
					>
						{summary}
					</span>
					<IconChevronDown />
				</button>
			</FilterField>

			{selectedOptions.length > 0 && (
				<div className="mt-2 flex flex-wrap gap-2">
					{selectedOptions.map(option => (
						<button
							key={option.id}
							type="button"
							className="inline-flex items-center gap-2 rounded-full border border-default-200 bg-default-100 px-3 py-1 text-xs font-medium text-default-700 transition hover:border-default-300 hover:bg-default-150 dark:border-default-700 dark:bg-default-800 dark:text-default-200 dark:hover:border-default-600"
							onClick={() => handleToggle(option.id)}
						>
							<span>{option.name}</span>
							<span className="text-[11px] text-default-500 dark:text-default-400">
								x
							</span>
						</button>
					))}
				</div>
			)}

			{open && (
				<div className="absolute left-0 right-0 z-30 mt-2 max-h-60 overflow-auto rounded-xl border border-default-200 bg-white p-2 shadow-lg dark:border-default-700 dark:bg-default-950">
					{options.length > 0 ? (
						options.map(option => {
							const checked = selectedIds.some(id => String(id) === String(option.id));

							return (
								<label
									key={option.id}
									className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 hover:bg-default-100 dark:hover:bg-default-800"
								>
									<input
										type="checkbox"
										className="size-4 rounded border-default-300 text-primary focus:ring-primary"
										checked={checked}
										onChange={() => handleToggle(option.id)}
									/>
									<span className="text-sm text-default-800 dark:text-default-100">
										{option.name}
									</span>
								</label>
							);
						})
					) : (
						<p className="mb-0 px-3 py-2 text-sm text-default-500 dark:text-default-400">
							{emptyText}
						</p>
					)}
				</div>
			)}
		</div>
	);
}

function SummaryCard({ title, value, hint, icon, toneClassName }) {
	return (
		<div className="card h-full">
			<div className="card-body flex items-start justify-between gap-4">
				<div>
					<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
						{title}
					</p>
					<p className="m-0 text-2xl font-semibold text-default-800 dark:text-default-100">
						{value}
					</p>
					<p className="mt-2 mb-0 text-sm text-default-500 dark:text-default-400">
						{hint}
					</p>
				</div>
				<div
					className={`flex size-12 shrink-0 items-center justify-center rounded-2xl ${toneClassName}`}
				>
					{icon}
				</div>
			</div>
		</div>
	);
}

function StatusBadge({ status }) {
	const meta = getStatusMeta(status);

	return (
		<span className={`inline-flex rounded px-2.5 py-0.5 text-xs font-medium ${meta.className}`}>
			{meta.label}
		</span>
	);
}

export default function Report() {
	const [filters, setFilters] = useState(DEFAULT_FILTERS);
	const initialFiltersRef = useRef(DEFAULT_FILTERS);
	const [users, setUsers] = useState([]);
	const [queues, setQueues] = useState([]);
	const [tags, setTags] = useState([]);
	const [connections, setConnections] = useState([]);
	const [reportData, setReportData] = useState({});
	const [loadingPreview, setLoadingPreview] = useState(false);
	const [exportingPdf, setExportingPdf] = useState(false);
	const [exportingExcel, setExportingExcel] = useState(false);

	const buildReportParams = useCallback(currentFilters => {
		return {
			searchParam: currentFilters.searchParam.trim(),
			period: currentFilters.period,
			status: currentFilters.status,
			groupBy: currentFilters.groupBy,
			userIds: JSON.stringify(currentFilters.userIds),
			queueIds: JSON.stringify(currentFilters.queueIds),
			connectionIds: JSON.stringify(currentFilters.connectionIds),
			tagIds: JSON.stringify(currentFilters.tagIds),
		};
	}, []);

	const requestReport = useCallback(
		async currentFilters => {
			const { data } = await api.get("/report", {
				params: buildReportParams(currentFilters),
			});

			if (!data?.tickets || typeof data.tickets !== "object") {
				return {};
			}

			return data.tickets;
		},
		[buildReportParams]
	);

	const loadPreview = useCallback(
		async currentFilters => {
			setLoadingPreview(true);
			try {
				const groupedTickets = await requestReport(currentFilters);
				setReportData(groupedTickets);
				return groupedTickets;
			} catch (err) {
				toastError(err);
				setReportData({});
				return null;
			} finally {
				setLoadingPreview(false);
			}
		},
		[requestReport]
	);

	const loadOptions = useCallback(async () => {
		try {
			const [
				{ data: usersData },
				{ data: queuesData },
				{ data: connectionsData },
				{ data: tagsData },
			] = await Promise.all([
				api.get("/users/"),
				api.get("/queue/"),
				api.get("/whatsapp/"),
				api.get("/tags/"),
			]);

			setUsers(Array.isArray(usersData?.users) ? usersData.users : []);
			setQueues(Array.isArray(queuesData) ? queuesData : []);
			setConnections(Array.isArray(connectionsData) ? connectionsData : []);
			setTags(Array.isArray(tagsData?.tags) ? tagsData.tags : []);
		} catch (err) {
			toastError(err);
		}
	}, []);

	useEffect(() => {
		loadOptions();
		loadPreview(initialFiltersRef.current);
	}, [loadOptions, loadPreview]);

	const updateFilter = (name, value) => {
		setFilters(prev => ({
			...prev,
			[name]: value,
		}));
	};

	const groupedEntries = useMemo(() => {
		return Object.entries(reportData)
			.map(([key, tickets]) => {
				const safeTickets = Array.isArray(tickets) ? tickets : [];
				const latestDate = safeTickets.reduce((latest, ticket) => {
					const current = ticket?.createdAt ? new Date(ticket.createdAt).getTime() : 0;
					return current > latest ? current : latest;
				}, 0);

				return {
					key,
					label: getGroupLabel(filters.groupBy, key),
					tickets: safeTickets,
					total: safeTickets.length,
					tagged: safeTickets.filter(ticket => (ticket?.contact?.tags || []).length > 0)
						.length,
					annotations: safeTickets.filter(ticket => (ticket?.messages || []).length > 0)
						.length,
					lastCreatedAt: latestDate ? new Date(latestDate).toISOString() : null,
				};
			})
			.filter(group => group.total > 0)
			.sort((a, b) => {
				if (a.key === "unassigned") return 1;
				if (b.key === "unassigned") return -1;
				return b.total - a.total;
			});
	}, [filters.groupBy, reportData]);

	const previewTickets = useMemo(() => {
		return groupedEntries
			.flatMap(group =>
				group.tickets.map(ticket => ({
					...ticket,
					__groupLabel: group.label,
				}))
			)
			.sort(
				(a, b) =>
					new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime()
			)
			.slice(0, 6);
	}, [groupedEntries]);

	const summary = useMemo(() => {
		return {
			totalTickets: groupedEntries.reduce((acc, group) => acc + group.total, 0),
			totalGroups: groupedEntries.length,
			taggedTickets: groupedEntries.reduce((acc, group) => acc + group.tagged, 0),
			annotatedTickets: groupedEntries.reduce((acc, group) => acc + group.annotations, 0),
			unassignedTickets: Array.isArray(reportData.unassigned)
				? reportData.unassigned.length
				: 0,
		};
	}, [groupedEntries, reportData]);

	const handleClearFilters = () => {
		setFilters(DEFAULT_FILTERS);
		loadPreview(DEFAULT_FILTERS);
	};

	const handleExportExcel = async () => {
		setExportingExcel(true);
		try {
			const groupedTickets = await requestReport(filters);
			setReportData(groupedTickets);
			ExcelPrint(groupedTickets);
		} catch (err) {
			toastError(err);
		} finally {
			setExportingExcel(false);
		}
	};

	const handleExportPdf = async () => {
		setExportingPdf(true);
		try {
			const groupedTickets = await requestReport(filters);
			setReportData(groupedTickets);
			exportPdfReport({
				ticketsByGroup: groupedTickets,
				groupBy: filters.groupBy,
				fileName: buildFileName("pdf"),
			});
		} catch (err) {
			toastError(err);
		} finally {
			setExportingPdf(false);
		}
	};

	return (
		<MainContainer>
			<div className="app-admin-page">
				<div className="app-admin-page__scroll">
					<AppPageTitle title="Relatórios" subtitle="Atendimento" />

					<div className="card mb-5">
						<div className="card-body space-y-5">
							<div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-[minmax(0,1.2fr)_220px_220px]">
								<FilterField
									label="Pesquisar"
									hint="Busca por contato, telefone, última mensagem, atendente, setor ou conexão."
								>
									<div className="relative">
										<input
											type="search"
											name="searchParam"
											value={filters.searchParam}
											onChange={e => updateFilter("searchParam", e.target.value)}
											onKeyDown={e => {
												if (e.key === "Enter") {
													loadPreview(filters);
												}
											}}
											className="form-input w-full ps-11"
											placeholder="Ex.: João, 1199..., cobrança..."
										/>
										<div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
											<IconSearch />
										</div>
									</div>
								</FilterField>

								<FilterField label="Período">
									<select
										className="form-select w-full"
										value={filters.period}
										onChange={e => updateFilter("period", e.target.value)}
									>
										{PERIOD_OPTIONS.map(option => (
											<option key={option.value} value={option.value}>
												{option.label}
											</option>
										))}
									</select>
								</FilterField>

								<FilterField label="Status">
									<select
										className="form-select w-full"
										value={filters.status}
										onChange={e => updateFilter("status", e.target.value)}
									>
										{STATUS_OPTIONS.map(option => (
											<option key={option.value} value={option.value}>
												{option.label}
											</option>
										))}
									</select>
								</FilterField>
							</div>

							<div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
								<MultiFilterSelect
									label="Atendentes"
									options={users}
									selectedIds={filters.userIds}
									onChange={value => updateFilter("userIds", value)}
								/>
								<MultiFilterSelect
									label="Setores"
									options={queues}
									selectedIds={filters.queueIds}
									onChange={value => updateFilter("queueIds", value)}
								/>
								<MultiFilterSelect
									label="Conexões"
									options={connections}
									selectedIds={filters.connectionIds}
									onChange={value => updateFilter("connectionIds", value)}
								/>
								<MultiFilterSelect
									label="Etiquetas"
									options={tags}
									selectedIds={filters.tagIds}
									onChange={value => updateFilter("tagIds", value)}
								/>
							</div>

							<div className="space-y-3">
								<div>
									<p className="mb-1 text-sm font-medium text-default-700 dark:text-default-200">
										Agrupar por
									</p>
									<p className="m-0 text-sm text-default-500 dark:text-default-400">
										Escolha como o relatório deve consolidar os tickets.
									</p>
								</div>
								<div className="flex flex-wrap gap-2">
									{GROUP_BY_OPTIONS.map(option => (
										<button
											key={option.value}
											type="button"
											className={`btn btn-sm border ${
												filters.groupBy === option.value
													? "border-primary bg-primary text-white"
													: "border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
											}`}
											onClick={() => updateFilter("groupBy", option.value)}
										>
											{option.label}
										</button>
									))}
								</div>
							</div>

							<div className="flex flex-wrap items-center justify-between gap-3 border-t border-default-200 pt-4 dark:border-default-700">
								<p className="m-0 text-sm text-default-500 dark:text-default-400">
									Use a prévia para validar o agrupamento antes de exportar.
								</p>
								<div className="flex flex-wrap gap-2">
									<button
										type="button"
										className="btn btn-sm btn-sub-gray"
										onClick={handleClearFilters}
									>
										<IconEraser />
										Limpar filtros
									</button>
									<button
										type="button"
										className="btn btn-sm bg-primary text-white"
										onClick={() => loadPreview(filters)}
										disabled={loadingPreview}
									>
										<IconRefresh />
										{loadingPreview ? "Atualizando..." : "Atualizar prévia"}
									</button>
									<button
										type="button"
										className="btn btn-sm btn-sub-gray"
										onClick={handleExportExcel}
										disabled={exportingExcel}
									>
										<IconSheet />
										{exportingExcel ? "Gerando Excel..." : "Exportar Excel"}
									</button>
									<button
										type="button"
										className="btn btn-sm btn-sub-gray"
										onClick={handleExportPdf}
										disabled={exportingPdf}
									>
										<IconDownload />
										{exportingPdf ? "Gerando PDF..." : "Exportar PDF"}
									</button>
								</div>
							</div>
						</div>
					</div>

					<div className="mb-5 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-5">
						<SummaryCard
							title="Tickets"
							value={summary.totalTickets}
							hint="Total encontrado na prévia atual."
							icon={<IconTicket />}
							toneClassName="bg-primary/10 text-primary"
						/>
						<SummaryCard
							title="Grupos"
							value={summary.totalGroups}
							hint="Quantidade de agrupamentos retornados."
							icon={<IconLayers />}
							toneClassName="bg-info/10 text-info"
						/>
						<SummaryCard
							title="Com etiquetas"
							value={summary.taggedTickets}
							hint="Tickets com pelo menos uma etiqueta."
							icon={<IconTag />}
							toneClassName="bg-warning/10 text-warning"
						/>
						<SummaryCard
							title="Com anotação"
							value={summary.annotatedTickets}
							hint="Tickets com mensagem interna na prévia."
							icon={<IconNote />}
							toneClassName="bg-success/10 text-success"
						/>
						<SummaryCard
							title="Sem atribuição"
							value={summary.unassignedTickets}
							hint="Tickets agrupados como não atribuídos."
							icon={<IconUserX />}
							toneClassName="bg-danger/10 text-danger"
						/>
					</div>

					{loadingPreview ? (
						<div className="app-modal-loading py-16">
							<div className="app-modal-spinner" />
						</div>
					) : groupedEntries.length === 0 ? (
						<div className="card">
							<div className="card-body flex flex-col items-center justify-center gap-2 py-16 text-center">
								<p className="m-0 font-semibold text-default-800 dark:text-default-100">
									Nenhum ticket encontrado para este relatório
								</p>
								<p className="m-0 text-sm text-default-500 dark:text-default-400">
									Tente outro período, ajuste os filtros ou mude o agrupamento.
								</p>
							</div>
						</div>
					) : (
						<div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
							<div className="card">
								<div className="card-header flex-wrap items-start justify-between gap-2 px-5 pt-5 pb-4">
									<div>
										<h3 className="card-title m-0 text-sm">Resumo por agrupamento</h3>
										<p className="mt-1 mb-0 text-sm text-default-500 dark:text-default-400">
											Veja onde está o maior volume antes de exportar.
										</p>
									</div>
								</div>
								<div className="card-body p-0">
									<div className="app-table-scroll">
										<table className="w-full min-w-[760px] table-auto">
											<thead>
												<tr className="bg-default-150">
													<th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-default-500">
														Agrupamento
													</th>
													<th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-default-500">
														Tickets
													</th>
													<th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-default-500">
														Etiquetas
													</th>
													<th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-default-500">
														Anotações
													</th>
													<th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-default-500">
														Último ticket
													</th>
												</tr>
											</thead>
											<tbody>
												{groupedEntries.map(group => (
													<tr key={group.key}>
														<td className="px-4 py-3 text-sm font-medium text-default-800 dark:text-default-100">
															{group.label}
														</td>
														<td className="px-4 py-3 text-sm text-default-700 dark:text-default-200">
															{group.total}
														</td>
														<td className="px-4 py-3 text-sm text-default-700 dark:text-default-200">
															{group.tagged}
														</td>
														<td className="px-4 py-3 text-sm text-default-700 dark:text-default-200">
															{group.annotations}
														</td>
														<td className="px-4 py-3 text-sm text-default-700 dark:text-default-200">
															{formatDateTime(group.lastCreatedAt)}
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</div>
							</div>

							<div className="card">
								<div className="card-header flex-wrap items-start justify-between gap-2 px-5 pt-5 pb-4">
									<div>
										<h3 className="card-title m-0 text-sm">Prévia dos tickets</h3>
										<p className="mt-1 mb-0 text-sm text-default-500 dark:text-default-400">
											Amostra rápida do conteúdo que será exportado.
										</p>
									</div>
									<span className="inline-flex rounded-full border border-default-200 bg-default-100 px-3 py-1 text-xs font-medium text-default-600 dark:border-default-700 dark:bg-default-800 dark:text-default-300">
										{previewTickets.length} de {summary.totalTickets} tickets
									</span>
								</div>
								<div className="card-body pt-2">
									<div className="divide-y divide-default-200 dark:divide-default-700">
										{previewTickets.map(ticket => (
											<div key={ticket.id} className="py-3 first:pt-0 last:pb-0">
												<div className="mb-2 flex items-start justify-between gap-3">
													<div className="min-w-0">
														<p className="m-0 truncate font-medium text-default-800 dark:text-default-100">
															#{ticket.id} · {ticket.contact?.name || "Sem contato"}
														</p>
														<p className="m-0 truncate text-xs text-default-500 dark:text-default-400">
															{ticket.contact?.number || "Sem telefone"} ·{" "}
															{ticket.__groupLabel}
														</p>
													</div>
													<StatusBadge status={ticket.status} />
												</div>

												<p className="mb-2 line-clamp-1 text-sm text-default-700 dark:text-default-200">
													{normalizeText(ticket.lastMessage) ||
														"Sem última mensagem registrada."}
												</p>

												<div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-default-500 dark:text-default-400">
													<span>{ticket.user?.name || "Sem atendente"}</span>
													<span>·</span>
													<span>{ticket.queue?.name || "Sem setor"}</span>
													<span>·</span>
													<span>{(ticket.contact?.tags || []).length} etiqueta(s)</span>
													<span>·</span>
													<span>{(ticket.messages || []).length} anotação(ões)</span>
												</div>

												<p className="mt-1 mb-0 text-xs text-default-500 dark:text-default-400">
													{formatDateTime(ticket.createdAt)}
												</p>
											</div>
										))}
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</MainContainer>
	);
}
